import PropTypes from 'prop-types';
import React, { useContext, useEffect, useRef, useState } from 'react';

import { GridBlock, GridCellSizeContext } from '$partials/grid/layout/index.js';

/* ========================================================================= */
/* Configuration */
/* ========================================================================= */

/* The block occupies a fixed three columns at every breakpoint */
const WIDTH = 3;

/* Used for the server render, until the content can be measured on the client */
const DEFAULT_HEIGHT = 3;

/* The aspect ratio matrix and safelisted span utilities only cover 1-9 */
const MAX_HEIGHT = 9;

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const GridTableBlock = ({
    copy,
    heading,
    moduleColour,
    table
}) => {
    const cellSize = useContext(GridCellSizeContext);
    const cellSizeRef = useRef(cellSize);
    const contentRef = useRef();
    const [measuredHeight, setMeasuredHeight] = useState(null);

    const measure = () => {
        if (!contentRef.current || !cellSizeRef.current) { return; }
        const rows = Math.ceil(contentRef.current.getBoundingClientRect().height / cellSizeRef.current);
        setMeasuredHeight(Math.min(MAX_HEIGHT, Math.max(1, rows)));
    };

    useEffect(() => {
        cellSizeRef.current = cellSize;
        measure();
    }, [cellSize]);

    useEffect(() => {
        if (!contentRef.current || typeof ResizeObserver !== 'function') { return () => {}; }

        const observer = new ResizeObserver(measure);
        observer.observe(contentRef.current);

        return () => observer.disconnect();
    }, []);

    return (
        <GridBlock
            bottom={0}
            height={Math.max(DEFAULT_HEIGHT, measuredHeight || 0)}
            left={0}
            right={0}
            top={0}
            width={WIDTH}>
            <div
                className='h-full w-full'
                style={{ backgroundColor: moduleColour }}>
                <div
                    className='px-20 pt-20 pb-60 sm:px-40 sm:pt-40 sm:pb-60 lg:px-60 lg:pt-60'
                    ref={contentRef}>
                    <h3 className='type-style-1 mb-20'>
                        {heading}
                    </h3>

                    {copy &&
                        <div
                            className='rich-text type-style-4 mb-40 md:mb-60 lg:mb-80 max-w-[36em]'
                            dangerouslySetInnerHTML={{ __html: copy }} />
                    }

                    {table &&
                        <table className='w-full type-style-4 max-w-[36em]'>
                            <tbody>
                                {table.map((row, i) => {
                                    return (
                                        <tr
                                            className='border-t border-midnight border-opacity-40 align-top'
                                            key={i}>
                                            <th
                                                className='type-style-4 text-left py-10 pr-10'
                                                dangerouslySetInnerHTML={{ __html: row.label }}
                                                scope='row' />
                                            <td
                                                className='type-style-4 text-left py-10 pl-10'
                                                dangerouslySetInnerHTML={{ __html: row.value }} />
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    }
                </div>
            </div>
        </GridBlock>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const propTypes = {
    copy: PropTypes.string,
    heading: PropTypes.string.isRequired,
    moduleColour: PropTypes.string,
    table: PropTypes.array.isRequired,
};

GridTableBlock.propTypes = propTypes;

export default GridTableBlock;
